import React, { useState, useRef, useContext } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Bot, Send, User, Loader2, ShieldAlert, ThumbsUp, ThumbsDown } from 'lucide-react';
import { Brand } from '@/components/ui/Brand';

export const SchoolzestAIWidget = () => {
  const { token, user } = useContext(AuthContext);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [feedbackGiven, setFeedbackGiven] = useState({});
  const chatEndRef = useRef(null);

  // IM8 GA-8: AI Consent Acknowledgement
  const consentKey = `ai-consent-${user?.id}`;
  const [aiConsented, setAiConsented] = useState(() => localStorage.getItem(consentKey) === 'true');

  const handleConsent = () => {
    localStorage.setItem(consentKey, 'true');
    setAiConsented(true);
  };

  const handleFeedback = async (msgIndex, feedback) => {
    if (!sessionId) return;
    setFeedbackGiven(prev => ({ ...prev, [msgIndex]: feedback }));
    try {
      await axios.post(`${API}/ai/feedback`, { session_id: sessionId, message_index: msgIndex, feedback }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch { /* silently fail */ }
  };

  const handleSend = async () => {
    const query = input.trim();
    if (!query || loading) return;

    setMessages(prev => [...prev, { role: 'user', text: query }]);
    setInput('');
    setLoading(true);

    try {
      const res = await axios.post(`${API}/ai/ask`, {
        query,
        session_id: sessionId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(prev => [...prev, { role: 'assistant', text: res.data.response }]);
      if (res.data.session_id) setSessionId(res.data.session_id);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', text: 'Sorry, something went wrong. Please try again.' }]);
    } finally {
      setLoading(false);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  };

  const suggestions = user?.role === 'admin'
    ? ["Who is absent today?", "Show relief assignments this week", "Which teachers are overloaded?", "Upcoming exam schedule"]
    : ["What are my duties this week?", "Am I covering any class today?", "Show my timetable", "Do I have invigilation duties?"];

  return (
    <Card className="dashboard-card overflow-hidden" data-testid="schoolzest-ai-card">
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 px-5 py-4 flex items-center gap-3">
        <div className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center">
          <Bot className="h-5 w-5 text-emerald-400" />
        </div>
        <div>
          <h3 className="text-white font-semibold text-sm">Ask <Brand theme="dark" suffix="AI" suffixTight suffixStyle={{ color: '#ffffff' }} /></h3>
          <p className="text-slate-400 text-xs">Your intelligent school management assistant</p>
        </div>
        <span className="ml-auto px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-[10px] font-medium rounded-full">PDPA Compliant</span>
      </div>

      {/* IM8 GA-8: Consent Gate */}
      {!aiConsented ? (
        <div className="p-6 bg-slate-50 text-center space-y-4" data-testid="ai-consent-gate">
          <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mx-auto">
            <ShieldAlert className="h-6 w-6 text-amber-600" />
          </div>
          <h4 className="text-sm font-semibold text-slate-900">AI Usage Acknowledgement</h4>
          <div className="text-xs text-slate-600 max-w-sm mx-auto space-y-2">
            <p><Brand suffix="AI" suffixTight /> is powered by generative AI. Before using it, please acknowledge the following:</p>
            <ul className="text-left space-y-1.5 pl-4">
              <li className="list-disc">AI responses may be <strong>inaccurate or incomplete</strong>. Always verify critical information.</li>
              <li className="list-disc">Your queries are processed with <strong>PDPA-compliant anonymization</strong> — no personal names are sent to external AI services.</li>
              <li className="list-disc">Do not input <strong>classified, sensitive, or NRIC/FIN</strong> information into the AI assistant.</li>
              <li className="list-disc">AI interactions are <strong>logged for audit purposes</strong> (IM8 LM-4 compliance).</li>
            </ul>
          </div>
          <Button
            onClick={handleConsent}
            className="bg-slate-900 hover:bg-slate-800 text-sm"
            data-testid="ai-consent-button"
          >
            I Understand &amp; Agree
          </Button>
        </div>
      ) : (
      <>
      <div className="h-72 overflow-y-auto p-4 space-y-3 bg-slate-50/50" data-testid="ai-chat-messages">
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center px-4">
            <Bot className="h-10 w-10 text-slate-300 mb-3" />
            <p className="text-sm text-slate-500 mb-4">Ask me anything about your schedule, duties, absences, or exams.</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  onClick={() => setInput(s)}
                  className="px-3 py-1.5 bg-white border border-slate-200 rounded-full text-xs text-slate-600 hover:border-slate-400 hover:text-slate-900 transition-colors"
                  data-testid={`ai-suggestion-${i}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((msg, i) => (
            <div key={i} className={`flex gap-2.5 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {msg.role === 'assistant' && (
                <div className="w-7 h-7 bg-slate-900 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Bot className="h-4 w-4 text-emerald-400" />
                </div>
              )}
              <div className={`max-w-[80%] px-3.5 py-2.5 rounded-xl text-sm leading-relaxed ${
                msg.role === 'user'
                  ? 'bg-slate-900 text-white rounded-br-sm'
                  : 'bg-white border border-slate-200 text-slate-700 rounded-bl-sm'
              }`} style={{ whiteSpace: 'pre-wrap' }} data-testid={`ai-msg-${msg.role}`}>
                {msg.text}
                {msg.role === 'assistant' && (
                  <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between">
                    <span className="text-[10px] text-slate-400 italic">AI-generated — may be inaccurate</span>
                    {!feedbackGiven[i] ? (
                      <div className="flex gap-1">
                        <button onClick={() => handleFeedback(i, 'positive')} className="p-1 hover:bg-emerald-50 rounded transition-colors" data-testid={`ai-feedback-up-${i}`} title="Helpful">
                          <ThumbsUp className="h-3 w-3 text-slate-400 hover:text-emerald-600" />
                        </button>
                        <button onClick={() => handleFeedback(i, 'negative')} className="p-1 hover:bg-red-50 rounded transition-colors" data-testid={`ai-feedback-down-${i}`} title="Not helpful">
                          <ThumbsDown className="h-3 w-3 text-slate-400 hover:text-red-500" />
                        </button>
                      </div>
                    ) : (
                      <span className="text-[10px] text-slate-400" data-testid={`ai-feedback-done-${i}`}>
                        {feedbackGiven[i] === 'positive' ? 'Thanks!' : 'Noted'}
                      </span>
                    )}
                  </div>
                )}
              </div>
              {msg.role === 'user' && (
                <div className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <User className="h-4 w-4 text-blue-700" />
                </div>
              )}
            </div>
          ))
        )}
        {loading && (
          <div className="flex gap-2.5">
            <div className="w-7 h-7 bg-slate-900 rounded-lg flex items-center justify-center flex-shrink-0">
              <Bot className="h-4 w-4 text-emerald-400" />
            </div>
            <div className="bg-white border border-slate-200 rounded-xl rounded-bl-sm px-4 py-3">
              <Loader2 className="h-4 w-4 text-slate-400 animate-spin" />
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      <div className="p-3 border-t border-slate-200 bg-white">
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Ask SchoolZest AI..."
            className="flex-1 h-10"
            disabled={loading}
            data-testid="ai-input"
          />
          <Button
            onClick={handleSend}
            disabled={!input.trim() || loading}
            className="bg-slate-900 hover:bg-slate-800 h-10 w-10 p-0"
            data-testid="ai-send-button"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-[10px] text-slate-400 mt-1.5 text-center"><Brand suffix="AI" suffixTight suffixStyle={{ color: '#94a3b8' }} /> uses anonymization to protect personal data. No identifying information is sent to external AI services.</p>
      </div>
      </>
      )}
    </Card>
  );
};
